import { type FC, useCallback, useEffect, useRef } from "react";
import { FocusTrap } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { type SweepstakesAnswerKey } from "@/utils/constants";
import {
  type PopupGift,
  type PopupLayout,
  type PopupOffer,
  type PopupVariant,
} from "@/utils/popupOffers";
import { popupCopy } from "./popupContent";
import { PopupTypeContext } from "./SweepstakesText";
import { POPUP_PANELS } from "./popupImages";
import { PANEL_IMAGE_SIZES } from "./popupPreload";
import IntroStep from "./steps/IntroStep";
import QuizStep from "./steps/QuizStep";
import EmailStep from "./steps/EmailStep";
import SuccessStep from "./steps/SuccessStep";
import { type SweepstakesStep } from "./types";

const fade = { duration: 0.75, ease: "easeOut" } as const;

// Backdrop-tap and Escape dismiss the popup on DESKTOP only. On mobile the card
// fills the screen, so accidental taps and scroll-starts on the surround were
// closing it — mobile closes via the X button only. 768 = matic `md` breakpoint.
const overlayDismissAllowed = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(min-width: 768px)").matches;

// Card: today's popup, sized to the viewport minus a stepped margin.
// Fullscreen: fills the viewport, photo on top until `split` and a 50/50 split from there.
// Stacked, the photo gives up height before the content would overflow.
const LAYOUT_CLASSES: Record<
  PopupLayout,
  {
    frame: string;
    card: string;
    scroller: string;
    mobilePhoto: string;
    desktopPhoto: string;
    content: string;
    step: string;
  }
> = {
  card: {
    frame: "items-center justify-center",
    card: "max-h-[calc(100%-36px)] w-[calc(100%-36px)] rounded-[24px] md:max-h-[calc(100%-48px)] md:w-[626px] md:max-w-[calc(100%-48px)] split:h-[544px] split:w-[1024px] split:max-w-[calc(100%-64px)] 2xl:h-[733px] 2xl:w-[1344px] 2xl:max-w-[calc(100%-80px)] 2xl:rounded-[32px]",
    scroller: "",
    mobilePhoto: "h-[206px] min-h-[120px] md:h-[352px]",
    desktopPhoto: "w-[512px] 2xl:w-[672px]",
    content:
      "flex-none p-[40px] md:p-[56px] split:flex-1 split:px-[48px] split:py-0 xl:px-[64px] 2xl:px-[80px]",
    step: "",
  },
  fullscreen: {
    frame: "items-stretch",
    card: "h-full w-full",
    scroller: "flex-1",
    mobilePhoto: "h-[34vh] min-h-[120px] md:h-[42vh]",
    desktopPhoto: "w-1/2",
    content:
      "flex-1 px-[32px] py-[40px] md:px-[56px] md:py-[56px] split:w-1/2 split:flex-none split:px-[56px] split:py-[48px] xl:px-[96px] xl:py-[64px] 2xl:px-[128px]",
    step: "max-w-[560px] 2xl:max-w-[640px] [@media(min-width:1920px)_and_(min-height:1080px)]:max-w-[760px]",
  },
};

// Gift compositions letterbox onto the same warm white their images use.
const PANEL_FIT = {
  cover: { frame: "", image: "object-cover" },
  contain: { frame: "bg-[#f5f3ef]", image: "object-contain" },
} as const;

type Props = {
  isOpen: boolean;
  variant: PopupVariant;
  layout: PopupLayout;
  offer: PopupOffer;
  gift: PopupGift | null;
  step: SweepstakesStep;
  onAdvance: () => void;
  onEmail: (email: string) => void;
  onAnswer: (answer: SweepstakesAnswerKey | null) => void;
  onClose: () => void;
  onShop: () => void;
};

const SweepstakesModal: FC<Props> = ({
  isOpen,
  variant,
  layout,
  offer,
  gift,
  step,
  onAdvance,
  onEmail,
  onAnswer,
  onClose,
  onShop,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const focusFrameRef = useRef<number | null>(null);

  const focusStepHeading = useCallback((node: HTMLDivElement | null) => {
    if (focusFrameRef.current !== null) {
      window.cancelAnimationFrame(focusFrameRef.current);
      focusFrameRef.current = null;
    }

    if (!node) return;

    focusFrameRef.current = window.requestAnimationFrame(() => {
      node
        .querySelector<HTMLElement>("[data-sweepstakes-step-heading]")
        ?.focus();
      focusFrameRef.current = null;
    });
  }, []);

  useEffect(
    () => () => {
      if (focusFrameRef.current !== null) {
        window.cancelAnimationFrame(focusFrameRef.current);
      }
    },
    [],
  );

  // A plain overlay (see the JSX comment) doesn't get Escape-to-close for free
  // the way a HeadlessUI <Dialog> did, so wire it up here.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && overlayDismissAllowed()) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const copy = popupCopy(offer, gift);
  const panel = POPUP_PANELS[variant];
  const classes = LAYOUT_CLASSES[layout];
  const typeScale = {
    fullscreen: layout === "fullscreen",
    balance: !(layout === "card" && offer.kind === "sweepstakes"),
  };

  const renderStep = () => {
    switch (step) {
      case "intro":
        return <IntroStep copy={copy.intro} onAdvance={onAdvance} />;
      case "quiz":
        return <QuizStep copy={copy.quiz} onAnswer={onAnswer} />;
      case "email":
        return <EmailStep copy={copy.email} onEmail={onEmail} />;
      case "success":
        return (
          <SuccessStep
            copy={copy.success}
            gift={gift}
            revealGift={offer.kind === "quiz"}
            onShop={onShop}
          />
        );
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        // A plain fixed overlay, NOT a HeadlessUI <Dialog>: the Dialog locks page
        // scroll by toggling `overflow` on <html>, which breaks position:fixed on
        // iOS Safari — the popup sticks to the locked layout viewport when you
        // pinch-zoom or pan. <FocusTrap> contains keyboard focus without touching
        // the document; `overscroll-contain` stops touch scroll from chaining to
        // the page behind.
        <motion.div
          key="sweepstakes"
          className="fixed inset-0 z-[60]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: fade }}
          exit={{ opacity: 0, transition: fade }}
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-black/70 backdrop-blur-[10px]"
          />
          <FocusTrap
            as="div"
            initialFocus={contentRef}
            role="dialog"
            aria-modal="true"
            aria-label={copy.a11y.dialog}
            className="absolute inset-0"
          >
            <div
              className={cn(
                "flex h-full w-full overflow-y-auto overscroll-contain",
                classes.frame,
              )}
              onClick={(event) => {
                if (
                  event.target === event.currentTarget &&
                  overlayDismissAllowed()
                )
                  onClose();
              }}
            >
              {/* The card no longer scrolls itself. Below `xl` the content runs
                  taller than a short viewport, and while the card was the
                  scroller the close button — absolutely positioned inside it —
                  rode off the top as you scrolled, with backdrop and Escape
                  dismissal being desktop-only. The scrolling moved to the
                  wrapper inside, so the button stays pinned to the card. */}
              <div
                className={cn(
                  "relative flex shrink-0 flex-col overflow-hidden bg-[#ffe895] split:flex-row",
                  classes.card,
                )}
              >
                <div
                  className={cn(
                    "flex min-h-0 w-full flex-col overflow-y-auto overscroll-contain split:h-full split:flex-row split:overflow-hidden",
                    classes.scroller,
                  )}
                >
                  <div
                    className={cn(
                      "relative w-full split:hidden",
                      classes.mobilePhoto,
                      PANEL_FIT[panel.fit].frame,
                    )}
                  >
                    <Image
                      src={panel.mobile}
                      alt=""
                      aria-hidden="true"
                      fill
                      sizes={PANEL_IMAGE_SIZES[layout].stacked}
                      className={PANEL_FIT[panel.fit].image}
                    />
                  </div>
                  <div
                    className={cn(
                      "relative hidden h-full shrink-0 split:block",
                      classes.desktopPhoto,
                      PANEL_FIT[panel.fit].frame,
                    )}
                  >
                    <Image
                      src={panel.desktop}
                      alt=""
                      aria-hidden="true"
                      fill
                      sizes={PANEL_IMAGE_SIZES[layout].split}
                      className={PANEL_FIT[panel.fit].image}
                    />
                  </div>
                  <div
                    ref={contentRef}
                    tabIndex={-1}
                    className={cn(
                      "relative z-10 flex w-full flex-col items-center text-center text-[#1f2022] outline-none split:h-full split:overflow-y-auto split:overscroll-contain",
                      classes.content,
                    )}
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        ref={focusStepHeading}
                        key={step}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className={cn(
                          "my-auto flex w-full flex-col items-center text-center",
                          classes.step,
                        )}
                      >
                        <PopupTypeContext.Provider value={typeScale}>
                          {renderStep()}
                        </PopupTypeContext.Provider>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  aria-label={copy.a11y.close}
                  className="absolute right-4 top-4 z-20 md:right-5 md:top-5 xl:right-6 xl:top-6 2xl:right-8 2xl:top-8"
                >
                  <X
                    className={cn(
                      "size-8 split:text-[#1f2022]",
                      panel.tone === "dark" ? "text-white" : "text-[#1f2022]",
                    )}
                  />
                </button>
              </div>
            </div>
          </FocusTrap>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SweepstakesModal;
